/**
 * `employees.*` — the payroll employee master (Payroll Increment 1).
 *
 * Master CRUD + archive-via-status, mirroring `vendors.*`. Employees are a
 * first-class master DISTINCT from `memberships` (app logins): the optional
 * `membershipId` link is null for the majority.
 * @see docs/product/payroll-v1-build-plan.md §"Employees ≠ members"
 *
 * PII (PAN / Aadhaar / bank account) is accepted as plaintext on create/update
 * and stored encrypted (V1 `v1!`-wrap seam) by the domain. Reads return PAN
 * full, Aadhaar masked, and bank account last-4 — unless `get({ reveal: true })`
 * (the edit-form path), which returns full Aadhaar + full bank account.
 *
 * Money is stringified integer paise on the wire (`moneyPaise`). List uses
 * cursor pagination (AGENTS §7 rule 14).
 */
import { z } from "zod";
import { uuid, paged, moneyPaise, isoDate, ifsc } from "../_common.js";

/** How the net salary is paid. Locked at the DB layer by a CHECK constraint. */
export const employeePayMode = z.enum(["neft", "upi", "cheque"]);

/** Active ⇄ inactive; 'inactive' is the archive. */
export const employeeStatus = z.enum(["active", "inactive"]);

/** Indian PAN — 5 letters + 4 digits + 1 letter. */
const pan = z.string().regex(/^[A-Z]{5}\d{4}[A-Z]$/, "invalid PAN (format: ABCDE1234F)");
/** Indian Aadhaar — exactly 12 digits. */
const aadhaar = z.string().regex(/^\d{12}$/, "invalid Aadhaar (12 digits)");
/** Indian bank account number — 9 to 18 digits. */
const bankAccount = z.string().regex(/^\d{9,18}$/, "invalid bank account number");

/**
 * Patchable master fields shared by create + update. PII fields are plaintext
 * on the wire; the domain wraps them before persisting.
 */
const baseFields = z.object({
  /** Full name — the only always-required field. Appears on the payslip. */
  name: z.string().trim().min(1, "Name is required").max(160),
  designation: z.string().trim().min(1).max(120).optional(),
  employeeCode: z.string().trim().min(1).max(40).optional(),
  pan: pan.optional(),
  aadhaar: aadhaar.optional(),
  /** Date of joining (YYYY-MM-DD). Required on create. */
  dateOfJoining: isoDate,
  /** Monthly gross in integer paise (stringified). Required. */
  monthlyGrossPaise: moneyPaise,
  /** Pre-filled PF deduction in paise. Defaults '0'. */
  defaultPfPaise: moneyPaise.optional(),
  /** Pre-filled Professional Tax deduction in paise. Defaults '0'. */
  defaultPtPaise: moneyPaise.optional(),
  payMode: employeePayMode.optional(),
  bankName: z.string().trim().min(1).max(120).optional(),
  bankAccount: bankAccount.optional(),
  bankIfsc: ifsc.optional(),
  /** Optional app-login link (a `memberships.id`). Null = no login. */
  membershipId: uuid.optional(),
});

// ── create ────────────────────────────────────────────────────────
export const createInput = baseFields;
export const createOutput = z.object({ id: uuid });

// ── get one ───────────────────────────────────────────────────────
export const getInput = z.object({
  id: uuid,
  /** When true, return full Aadhaar + full bank account (the edit-form path). */
  reveal: z.boolean().optional(),
});

/**
 * Full employee row as returned to the SPA. PAN is full; Aadhaar is masked
 * ("XXXX XXXX 1234") and bank account is last-4 ("······7782") on the normal
 * path; both are full when fetched with `reveal: true`.
 */
export const employeeRow = z.object({
  id: uuid,
  name: z.string(),
  designation: z.string().nullable(),
  employeeCode: z.string().nullable(),
  /** Full PAN (or null when not on file). */
  pan: z.string().nullable(),
  /** Masked "XXXX XXXX 1234" by default; full 12-digit when revealed. */
  aadhaar: z.string().nullable(),
  dateOfJoining: isoDate,
  monthlyGrossPaise: moneyPaise,
  defaultPfPaise: moneyPaise,
  defaultPtPaise: moneyPaise,
  payMode: employeePayMode,
  bankName: z.string().nullable(),
  /** Last-4 "······7782" by default; full account number when revealed. */
  bankAccount: z.string().nullable(),
  bankIfsc: z.string().nullable(),
  /** True iff a bank account number is on file (drives "has bank" affordances). */
  hasBankAccount: z.boolean(),
  status: employeeStatus,
  membershipId: uuid.nullable(),
  /** Resolved public URL for the profile picture; null = initials avatar. */
  photoUrl: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const getOutput = employeeRow;

// ── update (master fields only) ───────────────────────────────────
/** All fields optional; `id` selects the row. */
export const updateInput = baseFields.partial().extend({ id: uuid });
export const updateOutput = z.object({
  id: uuid,
  changedKeys: z.array(z.string()),
});

// ── set status (active ⇄ inactive; inactive IS the archive) ───────
export const setStatusInput = z.object({
  id: uuid,
  status: employeeStatus,
  reason: z.string().trim().max(255).optional(),
});
export const setStatusOutput = z.object({
  id: uuid,
  status: employeeStatus,
});

// ── list (cursor pagination) ──────────────────────────────────────
export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  q: z.string().optional(),
  /** `'all'` shows active + inactive; the status enum filters to one. */
  status: z.union([employeeStatus, z.literal("all")]).default("all"),
});

/**
 * One row in the list response. Lean — list views fetch many; PII is masked
 * (PAN full, bank last-4). No Aadhaar in the list (only on detail).
 */
export const listRow = z.object({
  id: uuid,
  name: z.string(),
  designation: z.string().nullable(),
  employeeCode: z.string().nullable(),
  pan: z.string().nullable(),
  bankName: z.string().nullable(),
  /** Last-4 "······7782" of the account number; null when none on file. */
  bankAccountLast4: z.string().nullable(),
  dateOfJoining: isoDate,
  monthlyGrossPaise: moneyPaise,
  payMode: employeePayMode,
  status: employeeStatus,
  membershipId: uuid.nullable(),
  /** Resolved public URL for the profile picture; null = initials avatar. */
  photoUrl: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export const listOutput = paged(listRow);

// ── profile picture (two-phase S3 upload) ─────────────────────────
export const photoContentType = z.enum(["image/jpeg", "image/png", "image/webp"]);

export const requestPhotoUploadInput = z.object({
  employeeId: uuid,
  contentType: photoContentType,
  size: z.number().int().positive(),
});
export const requestPhotoUploadOutput = z.object({
  uploadUrl: z.string().url(),
  objectKey: z.string(),
});

export const confirmPhotoInput = z.object({ employeeId: uuid, objectKey: z.string() });
export const confirmPhotoOutput = z.object({ id: uuid, photoUrl: z.string().nullable() });

export const removePhotoInput = z.object({ employeeId: uuid });
export const removePhotoOutput = z.object({ id: uuid });

// ── counts (KPI strip) ────────────────────────────────────────────
export const countsOutput = z.object({
  active: z.number().int().nonnegative(),
  inactive: z.number().int().nonnegative(),
  /** Sum of monthly_gross_paise across active employees (the payroll bill). */
  monthlyPayrollPaise: moneyPaise,
});

// ── derived TS types (consumed by domain + web) ───────────────────
export type EmployeePayMode = z.infer<typeof employeePayMode>;
export type EmployeeStatus = z.infer<typeof employeeStatus>;
export type CreateInput = z.infer<typeof createInput>;
export type CreateOutput = z.infer<typeof createOutput>;
export type GetInput = z.infer<typeof getInput>;
export type GetOutput = z.infer<typeof getOutput>;
export type UpdateInput = z.infer<typeof updateInput>;
export type UpdateOutput = z.infer<typeof updateOutput>;
export type SetStatusInput = z.infer<typeof setStatusInput>;
export type SetStatusOutput = z.infer<typeof setStatusOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type ListRow = z.infer<typeof listRow>;
export type EmployeeRow = z.infer<typeof employeeRow>;
export type CountsOutput = z.infer<typeof countsOutput>;
